import { supabase } from './supabaseClient';

// Definição dos níveis
export const NIVEIS = [
  { nivel: 1, nome: 'Iniciante', xpMinimo: 0, xpMaximo: 100, cor: 'gray' },
  { nivel: 2, nome: 'Focado', xpMinimo: 100, xpMaximo: 300, cor: 'blue' },
  { nivel: 3, nome: 'Determinado', xpMinimo: 300, xpMaximo: 600, cor: 'purple' },
  { nivel: 4, nome: 'Persistente', xpMinimo: 600, xpMaximo: 1000, cor: 'amber' },
  { nivel: 5, nome: 'Conquistador', xpMinimo: 1000, xpMaximo: 1500, cor: 'orange' },
  { nivel: 6, nome: 'Lenda', xpMinimo: 1500, xpMaximo: Infinity, cor: 'amber' },
];

// Definição das conquistas disponíveis
export const CONQUISTAS_DISPONIVEIS = [
  {
    tipo: 'primeiro_check',
    titulo: 'Primeiro Passo',
    descricao: 'Complete sua primeira etapa',
    icone: 'ri-checkbox-circle-line',
    xp_bonus: 10,
  },
  {
    tipo: 'cinco_etapas',
    titulo: 'Em Movimento',
    descricao: 'Complete 5 etapas',
    icone: 'ri-footprint-line',
    xp_bonus: 25,
  },
  {
    tipo: 'dez_etapas',
    titulo: 'Imparável',
    descricao: 'Complete 10 etapas',
    icone: 'ri-rocket-line',
    xp_bonus: 50,
  },
  {
    tipo: 'primeira_meta',
    titulo: 'Primeira Conquista',
    descricao: 'Complete sua primeira meta',
    icone: 'ri-trophy-line',
    xp_bonus: 100,
  },
  {
    tipo: 'tres_metas',
    titulo: 'Conquistador',
    descricao: 'Complete 3 metas',
    icone: 'ri-medal-line',
    xp_bonus: 200,
  },
  {
    tipo: 'cinco_metas',
    titulo: 'Mestre das Metas',
    descricao: 'Complete 5 metas',
    icone: 'ri-vip-crown-line',
    xp_bonus: 300,
  },
  {
    tipo: 'streak_3_dias',
    titulo: 'Consistente',
    descricao: 'Acesse o diário por 3 dias seguidos',
    icone: 'ri-fire-line',
    xp_bonus: 30,
  },
  {
    tipo: 'streak_7_dias',
    titulo: 'Dedicado',
    descricao: 'Acesse o diário por 7 dias seguidos',
    icone: 'ri-flashlight-line',
    xp_bonus: 75,
  },
  {
    tipo: 'streak_30_dias',
    titulo: 'Lendário',
    descricao: 'Acesse o diário por 30 dias seguidos',
    icone: 'ri-star-line',
    xp_bonus: 500,
  },
  {
    tipo: 'primeiro_desafio_semanal',
    titulo: 'Primeiro Desafio',
    descricao: 'Complete seu primeiro desafio semanal',
    icone: 'ri-sword-line',
    xp_bonus: 30,
  },
  {
    tipo: 'cinco_desafios_semanais',
    titulo: '5 Desafios Completos',
    descricao: 'Complete 5 desafios semanais ao longo do tempo',
    icone: 'ri-shield-star-line',
    xp_bonus: 100,
  },
  {
    tipo: 'mestre_desafios_semanais',
    titulo: 'Mestre dos Desafios',
    descricao: 'Complete todos os desafios de uma semana',
    icone: 'ri-vip-diamond-line',
    xp_bonus: 200,
  },
];

// Calcular nível baseado no XP
export function calcularNivel(xp: number) {
  for (let i = NIVEIS.length - 1; i >= 0; i--) {
    if (xp >= NIVEIS[i].xpMinimo) {
      return NIVEIS[i];
    }
  }
  return NIVEIS[0];
}

// Calcular progresso para o próximo nível
export function calcularProgressoNivel(xp: number) {
  const nivelAtual = calcularNivel(xp);
  const proximoNivel = NIVEIS.find(n => n.nivel === nivelAtual.nivel + 1);
  
  if (!proximoNivel) {
    return { progresso: 100, xpRestante: 0, proximoNivel: null };
  }

  const xpNoNivel = xp - nivelAtual.xpMinimo;
  const xpNecessario = proximoNivel.xpMinimo - nivelAtual.xpMinimo;
  const progresso = Math.round((xpNoNivel / xpNecessario) * 100);
  const xpRestante = proximoNivel.xpMinimo - xp;

  return { progresso, xpRestante, proximoNivel };
}

// Inicializar estatísticas do usuário
export async function inicializarEstatisticas(userId: string) {
  try {
    const { data: existente } = await supabase
      .from('estatisticas_diario')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (!existente) {
      const { error } = await supabase
        .from('estatisticas_diario')
        .insert({
          user_id: userId,
          xp_total: 0,
          nivel: 1,
          metas_criadas: 0,
          metas_concluidas: 0,
          etapas_concluidas: 0,
          streak_atual: 1,
          melhor_streak: 1,
          ultimo_acesso: new Date().toISOString().split('T')[0],
        });

      if (error) throw error;
    }
  } catch (error) {
    console.error('Erro ao inicializar estatísticas:', error);
  }
}

// Atualizar streak do usuário
export async function atualizarStreak(userId: string) {
  try {
    const { data: stats } = await supabase
      .from('estatisticas_diario')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (!stats) {
      await inicializarEstatisticas(userId);
      return;
    }

    const hoje = new Date().toISOString().split('T')[0];
    const ultimoAcesso = stats.ultimo_acesso;

    // Se já acessou hoje, não faz nada
    if (ultimoAcesso === hoje) return;

    const ontem = new Date();
    ontem.setDate(ontem.getDate() - 1);
    const ontemStr = ontem.toISOString().split('T')[0];

    let novoStreak = stats.streak_atual;

    // Se acessou ontem, incrementa streak
    if (ultimoAcesso === ontemStr) {
      novoStreak = stats.streak_atual + 1;
    } else {
      // Se não acessou ontem, reseta streak
      novoStreak = 1;
    }

    const melhorStreak = Math.max(novoStreak, stats.melhor_streak);

    // Atualizar no banco
    await supabase
      .from('estatisticas_diario')
      .update({
        streak_atual: novoStreak,
        melhor_streak: melhorStreak,
        ultimo_acesso: hoje,
      })
      .eq('user_id', userId);

    // Verificar conquistas de streak
    await verificarConquistasStreak(userId, novoStreak);
  } catch (error) {
    console.error('Erro ao atualizar streak:', error);
  }
}

// Adicionar XP ao usuário
export async function adicionarXP(userId: string, xp: number) {
  try {
    const { data: stats } = await supabase
      .from('estatisticas_diario')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (!stats) {
      await inicializarEstatisticas(userId);
      return;
    }

    const novoXP = stats.xp_total + xp;
    const nivelAtual = calcularNivel(stats.xp_total);
    const novoNivel = calcularNivel(novoXP);

    await supabase
      .from('estatisticas_diario')
      .update({
        xp_total: novoXP,
        nivel: novoNivel.nivel,
      })
      .eq('user_id', userId);

    // Se subiu de nível, retorna true
    return novoNivel.nivel > nivelAtual.nivel;
  } catch (error) {
    console.error('Erro ao adicionar XP:', error);
    return false;
  }
}

// Desbloquear conquista
export async function desbloquearConquista(userId: string, tipoConquista: string) {
  try {
    // Verificar se já tem essa conquista
    const { data: existente } = await supabase
      .from('conquistas_diario')
      .select('*')
      .eq('user_id', userId)
      .eq('tipo_conquista', tipoConquista)
      .maybeSingle();

    if (existente) return null; // Já tem essa conquista

    // Buscar dados da conquista
    const conquista = CONQUISTAS_DISPONIVEIS.find(c => c.tipo === tipoConquista);
    if (!conquista) return null;

    // Inserir conquista
    const { data, error } = await supabase
      .from('conquistas_diario')
      .insert({
        user_id: userId,
        tipo_conquista: tipoConquista,
        titulo: conquista.titulo,
        descricao: conquista.descricao,
        icone: conquista.icone,
        xp_bonus: conquista.xp_bonus,
      })
      .select()
      .single();

    if (error) throw error;

    // Adicionar XP bônus
    await adicionarXP(userId, conquista.xp_bonus);

    return data;
  } catch (error) {
    console.error('Erro ao desbloquear conquista:', error);
    return null;
  }
}

// Verificar conquistas de etapas
export async function verificarConquistasEtapas(userId: string, totalEtapas: number) {
  const conquistas = [];

  if (totalEtapas >= 1) {
    const c = await desbloquearConquista(userId, 'primeiro_check');
    if (c) conquistas.push(c);
  }
  if (totalEtapas >= 5) {
    const c = await desbloquearConquista(userId, 'cinco_etapas');
    if (c) conquistas.push(c);
  }
  if (totalEtapas >= 10) {
    const c = await desbloquearConquista(userId, 'dez_etapas');
    if (c) conquistas.push(c);
  }

  return conquistas;
}

// Verificar conquistas de metas
export async function verificarConquistasMetas(userId: string, totalMetas: number) {
  const conquistas = [];

  if (totalMetas >= 1) {
    const c = await desbloquearConquista(userId, 'primeira_meta');
    if (c) conquistas.push(c);
  }
  if (totalMetas >= 3) {
    const c = await desbloquearConquista(userId, 'tres_metas');
    if (c) conquistas.push(c);
  }
  if (totalMetas >= 5) {
    const c = await desbloquearConquista(userId, 'cinco_metas');
    if (c) conquistas.push(c);
  }

  return conquistas;
}

// Verificar conquistas de streak
export async function verificarConquistasStreak(userId: string, streak: number) {
  const conquistas = [];

  if (streak >= 3) {
    const c = await desbloquearConquista(userId, 'streak_3_dias');
    if (c) conquistas.push(c);
  }
  if (streak >= 7) {
    const c = await desbloquearConquista(userId, 'streak_7_dias');
    if (c) conquistas.push(c);
  }
  if (streak >= 30) {
    const c = await desbloquearConquista(userId, 'streak_30_dias');
    if (c) conquistas.push(c);
  }

  return conquistas;
}
